import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, Surface, Badge, Button, Modal, Input, Select, NewButton } from "@/components/ui-kit";
import { formatDate } from "@/lib/format";
import { Eye, EyeSlash, Key } from "@phosphor-icons/react";

const ROLES = [
    { value: "admin", label: "Admin" },
    { value: "fleet_manager", label: "Fleet Manager" },
    { value: "store_manager", label: "Store Manager" },
    { value: "technician", label: "Technician" },
    { value: "viewer", label: "Viewer (read-only)" },
];

const NOTIFY_FREQUENCIES = [
    { value: "off", label: "Off" },
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
];

const empty = { name: "", email: "", password: "", role: "fleet_manager", notify_frequency: "off" };

export default function Users() {
    const { user: me } = useAuth();
    const [list, setList] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);

    // Change-password modal state
    const [pwTarget, setPwTarget] = useState(null);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [pwSubmitting, setPwSubmitting] = useState(false);

    const load = () => api.get("/users").then((r) => setList(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            await api.post("/users", form);
            toast.success("User created");
            setOpen(false); setForm(empty); load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const del = async (id) => { if (!window.confirm("Delete user?")) return; await api.delete(`/users/${id}`); load(); };

    const setNotifyFrequency = async (u, freq) => {
        try {
            await api.patch(`/users/${u.id}/notify-frequency`, { notify_frequency: freq });
            setList((prev) => prev.map((x) => x.id === u.id ? { ...x, notify_frequency: freq } : x));
            if (freq === "off") {
                toast.success(`Email notifications turned off for ${u.email}`);
            } else {
                toast.success(`${u.name} will receive the ${freq} digest`);
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not update frequency");
        }
    };

    const openPwModal = (u) => {
        setPwTarget(u);
        setNewPassword("");
        setConfirmPassword("");
        setShowPassword(false);
    };

    const submitPasswordChange = async () => {
        if (newPassword.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error("Passwords don't match");
            return;
        }
        setPwSubmitting(true);
        try {
            await api.post(`/users/${pwTarget.id}/change-password`, { new_password: newPassword });
            toast.success(
                pwTarget.id === me?.id
                    ? "Your password has been updated"
                    : `Password updated for ${pwTarget.email}`
            );
            setPwTarget(null);
            setNewPassword("");
            setConfirmPassword("");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not update password");
        } finally {
            setPwSubmitting(false);
        }
    };

    return (
        <div className="p-6 lg:p-8 animate-fade-in">
            <PageHeader
                eyebrow="ADMIN"
                title="Users & Roles"
                subtitle={`${list.length} users · ${ROLES.length} roles`}
                action={<NewButton onClick={() => { setForm(empty); setOpen(true); }} label="Add user" testid="add-user-btn" />}
            />

            <Surface className="overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50"><tr className="text-left">
                        {["NAME", "EMAIL", "ROLE", "EMAIL NOTIFICATIONS", "CREATED", ""].map((h) => <th key={h} className="label-mono px-5 py-3 font-normal">{h}</th>)}
                    </tr></thead>
                    <tbody>
                        {list.map((u) => (
                            <tr key={u.id} className="border-t border-fleet-border data-grid-row" data-testid={`user-row-${u.id}`}>
                                <td className="px-5 py-3 font-medium">
                                    {u.name}
                                    {u.id === me?.id && <span className="ml-2 text-xs text-fleet-inkSoft">(you)</span>}
                                </td>
                                <td className="px-5 py-3 font-mono text-xs">{u.email}</td>
                                <td className="px-5 py-3"><Badge variant={u.role === "admin" ? "critical" : "info"}>{u.role.replace("_", " ")}</Badge></td>
                                <td className="px-5 py-3">
                                    {["admin", "fleet_manager"].includes(u.role) ? (
                                        <select
                                            value={u.notify_frequency || "off"}
                                            onChange={(e) => setNotifyFrequency(u, e.target.value)}
                                            className="px-2 py-1 text-xs border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink"
                                            data-testid={`notify-freq-${u.id}`}
                                            title="How often this user receives the consolidated operations digest email"
                                        >
                                            {NOTIFY_FREQUENCIES.map((f) => (
                                                <option key={f.value} value={f.value}>{f.label}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="text-xs text-fleet-inkSoft italic"
                                              title="Only Admin and Fleet Manager roles receive the digest">
                                            — not eligible
                                        </span>
                                    )}
                                </td>
                                <td className="px-5 py-3 font-mono text-xs">{formatDate(u.created_at)}</td>
                                <td className="px-5 py-3 text-right whitespace-nowrap">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => openPwModal(u)}
                                        data-testid={`change-password-${u.id}`}
                                    >
                                        <Key size={14} /> Change password
                                    </Button>
                                    {u.id !== me?.id && (
                                        <Button variant="ghost" size="sm" onClick={() => del(u.id)} data-testid={`delete-user-${u.id}`}>
                                            Delete
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {list.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-fleet-inkSoft">Loading…</td></tr>}
                    </tbody>
                </table>
            </Surface>

            <Modal open={open} onClose={() => setOpen(false)} title="Add user"
                footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-user">Create</Button></>}>
                <div className="grid grid-cols-2 gap-4">
                    <Input label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-user-name" />
                    <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="form-user-email" />
                    <Input label="Password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="form-user-password" />
                    <Select label="Role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} options={ROLES} data-testid="form-user-role" />
                    <div className="col-span-2">
                        <Select
                            label="Email notifications"
                            value={form.notify_frequency}
                            onChange={(e) => setForm({ ...form, notify_frequency: e.target.value })}
                            options={NOTIFY_FREQUENCIES}
                            data-testid="form-notify-frequency"
                        />
                        <div className="text-xs text-fleet-inkSoft mt-1">
                            How often this user receives the single consolidated operations digest. Only Admin + Fleet Manager roles currently receive the email.
                        </div>
                    </div>
                </div>
            </Modal>

            <Modal
                open={!!pwTarget}
                onClose={() => { setPwTarget(null); setNewPassword(""); setConfirmPassword(""); }}
                title={pwTarget ? `Change password · ${pwTarget.name}` : ""}
                footer={<>
                    <Button variant="secondary" onClick={() => { setPwTarget(null); setNewPassword(""); setConfirmPassword(""); }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={submitPasswordChange}
                        disabled={pwSubmitting || !newPassword || newPassword !== confirmPassword}
                        data-testid="submit-password-change"
                    >
                        {pwSubmitting ? "Updating…" : "Update password"}
                    </Button>
                </>}
            >
                {pwTarget && (
                    <div className="space-y-4">
                        <div className="bg-slate-50 border border-fleet-border rounded-md p-3 text-sm">
                            <div className="text-xs text-fleet-inkSoft uppercase tracking-wider mb-1">Account</div>
                            <div className="font-medium">{pwTarget.name}</div>
                            <div className="text-xs text-fleet-inkSoft font-mono">{pwTarget.email}</div>
                            {pwTarget.id === me?.id && (
                                <div className="text-xs text-amber-700 mt-2">
                                    You are changing your own password. You will stay signed in on this device.
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="label-mono block mb-1.5">New password</label>
                            <div className="relative">
                                <input
                                    type={showPassword ? "text" : "password"}
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    autoComplete="new-password"
                                    className="w-full px-3 py-2 pr-10 bg-white border border-fleet-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-fleet-ink focus:border-fleet-ink"
                                    data-testid="new-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((s) => !s)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-fleet-inkSoft hover:text-fleet-ink"
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    data-testid="toggle-new-password-visibility"
                                >
                                    {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                            <div className="text-xs text-fleet-inkSoft mt-1">Minimum 8 characters.</div>
                        </div>

                        <div>
                            <label className="label-mono block mb-1.5">Confirm password</label>
                            <input
                                type={showPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                autoComplete="new-password"
                                className="w-full px-3 py-2 bg-white border border-fleet-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-fleet-ink focus:border-fleet-ink"
                                data-testid="confirm-password-input"
                            />
                            {confirmPassword && newPassword !== confirmPassword && (
                                <div className="text-xs text-fleet-critical mt-1">Passwords don't match</div>
                            )}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
